import type {
  Client,
  ClientContact,
  CreateClientContactInput,
  PortalInviteResult,
} from '@pm/schemas';
import { displayDocument, displayPhone, formatPhone } from '@pm/schemas';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  EmptyState,
  ErrorState,
  Field,
  Input,
  LabelCaps,
  Menu,
  MenuContent,
  MenuItem,
  MenuSeparator,
  MenuTrigger,
  PageHeader,
  Skeleton,
  Tabs,
  Textarea,
} from '@pm/ui';
import {
  Ban,
  ChevronRight,
  Copy,
  KeyRound,
  Mail,
  MessageCircle,
  MoreHorizontal,
  Pencil,
  Phone,
  Plus,
  RotateCcw,
  Trash2,
  UserRound,
} from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { TaskCompactRow } from '@/features/tasks/task-bits';
import { TaskCreateDialog } from '@/features/tasks/TaskCreateDialog';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { fmtDateTime } from '@/lib/format';
import { copyText } from '@/features/forms/form-bits';
import { keys, useClient, useContacts, useInvalidating, useTasks } from '@/lib/queries';
import { useToast } from '@/lib/toast';
import { ClientDialog } from './ClientDialog';
import { ContextTeamSection } from './ContextTeamSection';
import { NotesSection, VaultSection } from './VaultSection';
import { CustomFieldsPanel } from '@/features/tasks/CustomFieldsPanel';

export function ClientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { can } = useAuth();
  const { data: client, isLoading, isError, error, refetch } = useClient(id);
  const [tab, setTab] = useState<'tasks' | 'contacts' | 'team' | 'notes' | 'vault' | 'data'>(
    'tasks',
  );
  const [edit, setEdit] = useState(false);
  const [newTask, setNewTask] = useState(false);
  const [confirm, setConfirm] = useState<null | 'delete' | 'force'>(null);

  const inval = [['clients'], ['contexts'], keys.client(id ?? '')];
  const del = useInvalidating(() => api.delete(`/clients/${id}`), inval);
  const restore = useInvalidating(() => api.post(`/clients/${id}/restore`), inval);
  const force = useInvalidating(
    (confirmation: string) => api.delete(`/clients/${id}/force`, { body: { confirmation } }),
    inval,
  );

  if (isLoading)
    return (
      <div className="flex flex-col gap-3">
        <Skeleton className="w-40" />
        <Skeleton className="h-6 w-1/2" />
        <Skeleton className="h-40 rounded-md" />
      </div>
    );
  if (isError || !client)
    return <ErrorState message={(error as Error)?.message} onRetry={() => void refetch()} />;

  const name = client.trade_name || client.legal_name;
  return (
    <div className="flex max-w-[1100px] flex-col gap-gap-lg">
      <PageHeader
        eyebrow={
          <div className="flex items-center gap-1.5 font-mono text-xs text-text-3">
            <Link to="/clientes" className="hover:text-text-1">
              Clientes
            </Link>
            <ChevronRight className="size-3" />
            <span>{client.person_type === 'COMPANY' ? 'PJ' : 'PF'}</span>
            {client.document ? (
              <>
                <span>·</span>
                <span>{displayDocument(client.document, client.person_type)}</span>
              </>
            ) : null}
          </div>
        }
        title={
          <span className="flex items-center gap-2">
            {name}
            {client.status === 'INACTIVE' ? <Badge tone="neutral">Inativo</Badge> : null}
            {client.deleted_at ? <Badge tone="danger">Na lixeira</Badge> : null}
          </span>
        }
        subtitle={client.trade_name ? client.legal_name : undefined}
        actions={
          <>
            {!client.deleted_at && can('tasks.create') ? (
              <Button variant="primary" onClick={() => setNewTask(true)}>
                <Plus /> Nova demanda
              </Button>
            ) : null}
            {client.deleted_at && can('clients.delete') ? (
              <Button
                variant="secondary"
                onClick={() =>
                  restore.mutate(undefined, {
                    onSuccess: () => toast.success('Cliente restaurado'),
                    onError: (e) => toast.error(e),
                  })
                }
              >
                <RotateCcw /> Restaurar
              </Button>
            ) : null}
            <Menu>
              <MenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Mais">
                  <MoreHorizontal />
                </Button>
              </MenuTrigger>
              <MenuContent align="end">
                {can('clients.edit') && !client.deleted_at ? (
                  <MenuItem onSelect={() => setEdit(true)}>
                    <Pencil /> Editar dados
                  </MenuItem>
                ) : null}
                <MenuSeparator />
                {can('clients.delete') && !client.deleted_at ? (
                  <MenuItem danger onSelect={() => setConfirm('delete')}>
                    <Trash2 /> Mover para lixeira
                  </MenuItem>
                ) : null}
                {can('clients.force_delete') && client.deleted_at ? (
                  <MenuItem danger onSelect={() => setConfirm('force')}>
                    <Trash2 /> Excluir definitivamente
                  </MenuItem>
                ) : null}
              </MenuContent>
            </Menu>
          </>
        }
      />
      <Tabs
        value={tab}
        onChange={setTab}
        tabs={[
          { value: 'tasks', label: 'Demandas', count: client.open_tasks },
          { value: 'contacts', label: 'Contatos', count: client.contacts_count },
          { value: 'team', label: 'Equipe' },
          { value: 'notes', label: 'Notas' },
          { value: 'vault', label: 'Cofre' },
          { value: 'data', label: 'Dados' },
        ]}
      />
      {tab === 'tasks' ? (
        <ClientTasks contextId={client.context_id} />
      ) : tab === 'contacts' ? (
        <Contacts client={client} />
      ) : tab === 'team' ? (
        <ContextTeamSection contextId={client.context_id} />
      ) : tab === 'notes' ? (
        <NotesSection contextId={client.context_id} />
      ) : tab === 'vault' ? (
        <VaultSection contextId={client.context_id} />
      ) : (
        <ClientData client={client} />
      )}

      <ClientDialog open={edit} onOpenChange={setEdit} client={client} />
      <TaskCreateDialog
        open={newTask}
        onOpenChange={setNewTask}
        defaultContextId={client.context_id}
      />
      <ConfirmDialog
        open={confirm === 'delete'}
        onOpenChange={() => setConfirm(null)}
        title="Mover cliente para a lixeira"
        description="O cliente e seu contexto saem das listas. Demandas existentes continuam."
        confirmLabel="Mover"
        danger
        loading={del.isPending}
        onConfirm={() =>
          del.mutate(undefined, {
            onSuccess: () => (toast.success('Movido para a lixeira'), setConfirm(null)),
            onError: (e) => toast.error(e),
          })
        }
      />
      <ConfirmDialog
        open={confirm === 'force'}
        onOpenChange={() => setConfirm(null)}
        title="Excluir cliente definitivamente"
        description="Contatos serão apagados. Demandas ficam sem contexto. Não pode ser desfeito."
        confirmLabel="Excluir"
        confirmText={client.legal_name}
        danger
        loading={force.isPending}
        onConfirm={() =>
          force.mutate(client.legal_name, {
            onSuccess: () => (toast.success('Cliente excluído'), navigate('/clientes')),
            onError: (e) => toast.error(e),
          })
        }
      />
    </div>
  );
}

function ClientTasks({ contextId }: { contextId: string }) {
  const { data, isLoading } = useTasks({
    context_id: contextId,
    per_page: 100,
    status: ['NOT_STARTED', 'IN_PROGRESS', 'WAITING', 'BLOCKED', 'PENDING_APPROVAL'],
  });
  const items = data?.data ?? [];
  return (
    <div className="rounded-md border border-border bg-surface-1">
      {isLoading ? (
        <div className="p-4">
          <Skeleton className="w-2/3" />
        </div>
      ) : items.length === 0 ? (
        <EmptyState icon={UserRound} title="Nenhuma demanda aberta" className="py-6" />
      ) : (
        items.map((t) => <TaskCompactRow key={t.id} task={t} />)
      )}
    </div>
  );
}

function Contacts({ client }: { client: Client }) {
  const toast = useToast();
  const { can } = useAuth();
  const { data: contacts = [], isLoading } = useContacts(client.id);
  const [editing, setEditing] = useState<ClientContact | 'new' | null>(null);
  const [inviteResult, setInviteResult] = useState<
    (PortalInviteResult & { contact: ClientContact }) | null
  >(null);
  const inval = [keys.contacts(client.id), keys.client(client.id), ['clients']];
  const invite = useInvalidating(
    (contactId: string) =>
      api.post<PortalInviteResult>(`/clients/${client.id}/contacts/${contactId}/portal/invite`),
    inval,
  );
  const revoke = useInvalidating(
    (contactId: string) => api.delete(`/clients/${client.id}/contacts/${contactId}/portal`),
    inval,
  );
  const save = useInvalidating(
    ({ id, input }: { id?: string; input: CreateClientContactInput }) =>
      id ? api.patch(`/contacts/${id}`, input) : api.post(`/clients/${client.id}/contacts`, input),
    inval,
  );
  const remove = useInvalidating((id: string) => api.delete(`/contacts/${id}`), inval);

  return (
    <div className="flex flex-col gap-3">
      {can('clients.edit') && !client.deleted_at ? (
        <div className="flex justify-end">
          <Button variant="secondary" size="sm" onClick={() => setEditing('new')}>
            <Plus /> Contato
          </Button>
        </div>
      ) : null}
      <div className="rounded-md border border-border bg-surface-1">
        {isLoading ? (
          <div className="p-4">
            <Skeleton className="w-1/2" />
          </div>
        ) : contacts.length === 0 ? (
          <EmptyState
            icon={UserRound}
            title="Nenhum contato"
            description="Contatos originam solicitações externas e recebem notificações."
            className="py-6"
          />
        ) : (
          <ul>
            {contacts.map((c) => (
              <li
                key={c.id}
                className="flex items-center gap-4 border-b border-border px-4 py-2.5 last:border-b-0"
              >
                <div className="min-w-0 flex-1">
                  <p className="font-medium">
                    {c.name}
                    {c.role ? <span className="ml-2 text-sm text-text-2">{c.role}</span> : null}
                  </p>
                  <p className="flex flex-wrap gap-3 font-mono text-xs text-text-3">
                    {c.email ? (
                      <span className="inline-flex items-center gap-1">
                        <Mail className="size-3" />
                        {c.email}
                      </span>
                    ) : null}
                    {c.phone ? (
                      <span className="inline-flex items-center gap-1">
                        <Phone className="size-3" />
                        {displayPhone(c.phone)}
                      </span>
                    ) : null}
                    {c.whatsapp ? (
                      <span className="inline-flex items-center gap-1">
                        <MessageCircle className="size-3" />
                        {c.whatsapp}
                      </span>
                    ) : null}
                  </p>
                </div>
                {c.status === 'INACTIVE' ? <Badge tone="neutral">Inativo</Badge> : null}
                {c.portal.enabled ? (
                  <Badge
                    tone="success"
                    title={
                      c.portal.last_login_at
                        ? `último acesso ${fmtDateTime(c.portal.last_login_at)}`
                        : 'nunca acessou'
                    }
                  >
                    portal
                  </Badge>
                ) : c.portal.invite_expires_at ? (
                  <Badge tone="info">convite enviado</Badge>
                ) : null}
                {can('clients.edit') ? (
                  <Menu>
                    <MenuTrigger asChild>
                      <Button variant="ghost" size="icon-sm" aria-label="Ações">
                        <MoreHorizontal />
                      </Button>
                    </MenuTrigger>
                    <MenuContent align="end">
                      <MenuItem onSelect={() => setEditing(c)}>
                        <Pencil /> Editar
                      </MenuItem>
                      {c.email ? (
                        <MenuItem
                          onSelect={() =>
                            invite.mutate(c.id, {
                              onSuccess: (r) => setInviteResult({ contact: c, ...r }),
                              onError: (e) => toast.error(e),
                            })
                          }
                        >
                          <KeyRound />{' '}
                          {c.portal.enabled
                            ? 'Reenviar acesso ao portal'
                            : 'Liberar acesso ao portal'}
                        </MenuItem>
                      ) : null}
                      {c.portal.enabled ? (
                        <MenuItem
                          onSelect={() =>
                            revoke.mutate(c.id, {
                              onSuccess: () => toast.success('Acesso ao portal revogado'),
                              onError: (e) => toast.error(e),
                            })
                          }
                        >
                          <Ban /> Revogar acesso ao portal
                        </MenuItem>
                      ) : null}
                      <MenuItem
                        danger
                        onSelect={() => remove.mutate(c.id, { onError: (e) => toast.error(e) })}
                      >
                        <Trash2 /> Remover
                      </MenuItem>
                    </MenuContent>
                  </Menu>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>
      <Dialog open={inviteResult !== null} onOpenChange={(o) => !o && setInviteResult(null)}>
        <DialogContent title="Acesso ao portal" size="sm">
          {inviteResult ? (
            <div className="flex flex-col gap-3 text-sm">
              <p>
                {inviteResult.sent_by_email
                  ? `Enviamos o convite para ${inviteResult.contact.email}.`
                  : 'E-mail não configurado nesta instalação: envie o link abaixo ao contato.'}{' '}
                Vale até {fmtDateTime(inviteResult.expires_at)}.
              </p>
              <code className="break-all rounded bg-surface-2 px-2 py-1 font-mono text-xs">
                {inviteResult.invite_url}
              </code>
              <DialogFooter>
                <Button
                  variant="secondary"
                  onClick={() =>
                    void copyText(inviteResult.invite_url).then((ok) =>
                      ok ? toast.success('Link copiado') : toast.error('Não foi possível copiar'),
                    )
                  }
                >
                  <Copy /> Copiar link
                </Button>
                <Button variant="primary" onClick={() => setInviteResult(null)}>
                  Fechar
                </Button>
              </DialogFooter>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
      <ContactDialog
        open={editing !== null}
        contact={editing === 'new' ? undefined : (editing ?? undefined)}
        onOpenChange={(o) => !o && setEditing(null)}
        loading={save.isPending}
        onSubmit={(input) =>
          save.mutate(
            { id: editing && editing !== 'new' ? editing.id : undefined, input },
            {
              onSuccess: () => (toast.success('Contato salvo'), setEditing(null)),
              onError: (e) => toast.error(e),
            },
          )
        }
      />
    </div>
  );
}

function ContactDialog({
  open,
  onOpenChange,
  contact,
  onSubmit,
  loading,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  contact?: ClientContact;
  onSubmit: (i: CreateClientContactInput) => void;
  loading: boolean;
}) {
  const [f, setF] = useState({
    name: contact?.name ?? '',
    role: contact?.role ?? '',
    email: contact?.email ?? '',
    phone: displayPhone(contact?.phone),
    whatsapp: contact?.whatsapp ?? '',
    notes: contact?.notes ?? '',
  });
  const set =
    (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setF({ ...f, [k]: e.target.value });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title={contact ? 'Editar contato' : 'Novo contato'} size="sm">
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit({
              name: f.name,
              role: f.role || undefined,
              email: f.email,
              phone: f.phone || undefined,
              whatsapp: f.whatsapp || undefined,
              notes: f.notes || undefined,
            });
          }}
        >
          <Field label="Nome" htmlFor="ct-name">
            <Input id="ct-name" autoFocus value={f.name} onChange={set('name')} />
          </Field>
          <Field label="Cargo" htmlFor="ct-role">
            <Input id="ct-role" value={f.role} onChange={set('role')} />
          </Field>
          <Field label="E-mail" htmlFor="ct-email">
            <Input id="ct-email" type="email" value={f.email} onChange={set('email')} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Telefone" htmlFor="ct-phone">
              <Input
                id="ct-phone"
                type="tel"
                mono
                value={f.phone}
                onChange={(e) => setF({ ...f, phone: formatPhone(e.target.value) })}
                placeholder="(11) 98765-4321"
                maxLength={19}
              />
            </Field>
            <Field label="WhatsApp" htmlFor="ct-wa">
              <Input id="ct-wa" mono value={f.whatsapp} onChange={set('whatsapp')} />
            </Field>
          </div>
          <Field label="Observações" htmlFor="ct-notes">
            <Textarea
              id="ct-notes"
              value={f.notes}
              onChange={set('notes')}
              className="min-h-[72px]"
            />
          </Field>
          <DialogFooter>
            <Button variant="ghost" type="button" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              type="submit"
              loading={loading}
              disabled={f.name.trim().length < 2}
            >
              Salvar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ClientData({ client }: { client: Client }) {
  const { can } = useAuth();
  const canEditClient = can('clients.edit');
  const a = client.address;
  const row = (label: string, value: React.ReactNode, mono = true) => (
    <>
      <dt className="text-text-3">{label}</dt>
      <dd className={mono ? 'font-mono' : ''}>{value || <span className="text-text-3">—</span>}</dd>
    </>
  );
  return (
    <div className="grid gap-gap-lg lg:grid-cols-2">
      <section className="rounded-md border border-border bg-surface-1 p-4 lg:col-span-2">
        <CustomFieldsPanel entity="CLIENT" id={client.id} canEdit={canEditClient} />
      </section>
      <section className="rounded-md border border-border bg-surface-1 p-4">
        <LabelCaps>Identificação</LabelCaps>
        <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
          {row(
            client.person_type === 'COMPANY' ? 'Razão social' : 'Nome',
            client.legal_name,
            false,
          )}
          {client.person_type === 'COMPANY' ? row('Nome fantasia', client.trade_name, false) : null}
          {row(
            client.person_type === 'COMPANY' ? 'CNPJ' : 'CPF',
            client.document ? displayDocument(client.document, client.person_type) : null,
          )}
          {client.person_type === 'COMPANY'
            ? row('Insc. estadual', client.state_registration)
            : null}
          {client.person_type === 'COMPANY'
            ? row('Insc. municipal', client.municipal_registration)
            : null}
          {row('E-mail', client.email)}
          {row('Telefone', client.phone ? displayPhone(client.phone) : null)}
          {row(
            'Site',
            client.website ? (
              <a
                href={client.website}
                target="_blank"
                rel="noreferrer"
                className="text-accent underline"
              >
                {client.website}
              </a>
            ) : null,
          )}
          {row('Cadastrado em', fmtDateTime(client.created_at))}
        </dl>
      </section>
      <section className="rounded-md border border-border bg-surface-1 p-4">
        <LabelCaps>Endereço</LabelCaps>
        {a && Object.values(a).some(Boolean) ? (
          <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
            {row('Logradouro', [a.street, a.number].filter(Boolean).join(', '), false)}
            {row('Complemento', a.complement, false)}
            {row('Bairro', a.district, false)}
            {row('Cidade', [a.city, a.state].filter(Boolean).join(' / '), false)}
            {row('CEP', a.zip)}
          </dl>
        ) : (
          <p className="mt-3 text-sm text-text-3">Sem endereço cadastrado.</p>
        )}
      </section>
    </div>
  );
}
